import type { ImportSignResponse, BucketUploadResult } from './entity';

/**
 * Direct-to-bucket file upload for imports.
 *
 * The upload goes straight to the cloud bucket using the short-lived signature
 * from `imports/sign`, and deliberately bypasses the `hydraUiApi` client:
 *
 *   - `method: 'POST'` multipart FormData.
 *   - Append every `signature.fields[k]` first, then append `file` LAST — the
 *     bucket policy is order-sensitive (the `file` part must come after the
 *     policy fields). Send ONLY the signed fields: the imports policy has no
 *     `content-type` condition, so adding a `Content-Type` field makes S3 reject
 *     the POST ("Extra input fields: content-type").
 *   - NO `Authorization` header and NO request `Content-Type` header — the
 *     browser sets the multipart boundary, and the request is authorized by the
 *     signature fields, not our session. Setting either header breaks the POST.
 *
 * Key derivation differs by provider:
 *   - GCS: `encodeURI(`${url}${fields.key}`)` — string concat, where
 *     `encodeURI` matches the filename re-encoding GCS does server-side.
 *   - S3: parse `<Location>` out of the XML success response.
 */

export class BucketUploadError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'BucketUploadError';
    this.status = status;
  }
}

interface SignatureFields {
  url: string;
  fields: Record<string, string>;
}

function normalizeSignature(sign: ImportSignResponse): { provider: 'gcs' | 's3'; signature: SignatureFields } {
  if (sign.gcs?.url != null) {
    return {
      provider: 'gcs',
      signature: { url: sign.gcs.url, fields: sign.gcs.fields ?? {} },
    };
  }

  if (sign.s3 != null) {
    // The S3 signature is a flat object of policy fields (bucket, acl, key,
    // policy, signature, …). The upload URL is the bucket endpoint; the
    // browser POSTs the fields as the form body.
    const { bucket, ...rest } = sign.s3;
    const fields: Record<string, string> = {};
    for (const [key, value] of Object.entries(rest)) {
      if (value != null) fields[key] = String(value);
    }
    return {
      provider: 's3',
      signature: { url: `https://s3.amazonaws.com/${bucket ?? ''}`, fields },
    };
  }

  throw new BucketUploadError('Signed upload response is missing both gcs and s3 signatures.', 0);
}

function extractXmlText(xml: Document, tagName: string): string | null {
  return xml.getElementsByTagName(tagName)[0]?.textContent ?? null;
}

/**
 * Upload `file` to the signed bucket URL and return the object key that the
 * subsequent `imports/create` call uses as `import.path`.
 */
export async function uploadToSignedBucket(file: File, sign: ImportSignResponse): Promise<BucketUploadResult> {
  const { provider, signature } = normalizeSignature(sign);

  const body = new FormData();
  // Send exactly the signed policy fields — no more. The imports `sign` policy
  // does NOT include a `content-type` condition, so adding a `Content-Type` form
  // field makes S3 reject the POST with `Invalid according to Policy: Extra
  // input fields: content-type`. (Only send Content-Type when the sign response
  // itself carries it, which imports don't.)
  for (const [key, value] of Object.entries(signature.fields)) {
    body.append(key, value);
  }
  // `file` MUST be appended last — bucket POST policies reject it otherwise.
  body.append('file', file);

  // No Authorization header and no request Content-Type header — the signature
  // fields authorize the POST and the browser owns the multipart boundary.
  const response = await fetch(signature.url, { method: 'POST', body });
  const text = await response.text();

  if (!response.ok) {
    const xml = new DOMParser().parseFromString(text, 'text/xml');
    const detail =
      extractXmlText(xml, 'Message') ?? `Something went wrong while uploading your file (${provider.toUpperCase()}).`;
    throw new BucketUploadError(detail, response.status);
  }

  if (provider === 'gcs') {
    return { key: encodeURI(`${signature.url}${signature.fields.key ?? ''}`) };
  }

  // S3 returns the object location in the XML success body.
  const xml = new DOMParser().parseFromString(text, 'text/xml');
  const location = extractXmlText(xml, 'Location');
  if (location == null || location === '') {
    throw new BucketUploadError('S3 upload succeeded but returned no Location.', response.status);
  }
  return { key: location };
}
